import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useApi, errMsg, fmtIST } from "@/lib/useApi";
import { Sel, Field } from "./QuestionEditor";
import { Plus, Trash2 } from "lucide-react";

export default function Syllabus() {
  const api = useApi();
  const [family, setFamily] = useState("UGC_NET");
  const [subjects, setSubjects] = useState([]);
  const [scope, setScope] = useState("UGC_PAPER1");
  const [doc, setDoc] = useState(null);

  useEffect(() => { api.get(`/subjects?family=${family}`).then((r) => setSubjects(r.data)); setScope(family === "UGC_NET" ? "UGC_PAPER1" : "CSIR_PARTA"); }, [family, api]);
  useEffect(() => { api.get(`/syllabus/${encodeURIComponent(scope)}`).then((r) => setDoc({ ...r.data, units: r.data.units.map((u) => ({ ...u, sub: u.subtopics.join("\n") })) })); }, [scope, api]);

  const setUnit = (i, k, v) => setDoc((d) => ({ ...d, units: d.units.map((u, j) => (j === i ? { ...u, [k]: v } : u)) }));
  const save = async () => {
    try {
      const r = await api.put(`/admin/syllabus/${encodeURIComponent(scope)}`, {
        units: doc.units.filter((u) => u.name.trim()).map((u) => ({ id: u.id, name: u.name, subtopics: u.sub.split(/\n|,/) })),
        source_url: doc.source_url || "", version_label: doc.version_label || "",
      });
      setDoc({ ...r.data, units: r.data.units.map((u) => ({ ...u, sub: u.subtopics.join("\n") })) });
      toast.success(`Saved as version ${r.data.version}`);
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="space-y-6 max-w-5xl" data-testid="admin-syllabus-page">
      <div>
        <h1 className="font-display font-bold text-2xl sm:text-3xl text-slate-900 tracking-tight">Syllabus, units & subtopics</h1>
        <p className="text-slate-600 mt-2 text-sm">Shared pools have one syllabus used by every subject in the family. Every save keeps the previous version.</p>
      </div>
      <Card className="p-5 border-slate-200 grid sm:grid-cols-2 gap-3">
        <Field l="Exam family"><Sel value={family} onChange={setFamily} options={[["UGC_NET", "UGC NET"], ["CSIR_NET", "CSIR NET"]]} testid="syl-family" /></Field>
        <Field l="Scope"><Sel value={scope} onChange={setScope} testid="syl-scope"
          options={[family === "UGC_NET" ? ["UGC_PAPER1", "Paper I — shared pool"] : ["CSIR_PARTA", "Part A — shared pool"], ...subjects.map((s) => [`${family}:${s.code}`, `${s.code} · ${s.name.slice(0, 60)}`])]} /></Field>
      </Card>
      {doc && <Card className="p-5 border-slate-200 space-y-4">
        <p className="text-xs text-slate-500">Version {doc.version || 0}{doc.updated_at ? ` · updated ${fmtIST(doc.updated_at)} by ${doc.updated_by}` : " · not yet defined"}</p>
        <div className="grid sm:grid-cols-2 gap-3">
          <Field l="Official syllabus URL"><Input value={doc.source_url || ""} onChange={(e) => setDoc({ ...doc, source_url: e.target.value })} data-testid="syl-source" /></Field>
          <Field l="Version label"><Input value={doc.version_label || ""} onChange={(e) => setDoc({ ...doc, version_label: e.target.value })} data-testid="syl-label" /></Field>
        </div>
        {doc.units.map((u, i) => (
          <div key={u.id || i} className="grid sm:grid-cols-[1fr_1.4fr_auto] gap-2 items-start border-t border-slate-100 pt-3">
            <Input value={u.name} placeholder={`Unit ${i + 1}`} onChange={(e) => setUnit(i, "name", e.target.value)} data-testid={`syl-unit-${i}`} />
            <Textarea rows={2} value={u.sub} placeholder="Subtopics (one per line)" onChange={(e) => setUnit(i, "sub", e.target.value)} />
            <Button size="icon" variant="ghost" onClick={() => setDoc({ ...doc, units: doc.units.filter((_, j) => j !== i) })}><Trash2 className="h-4 w-4" /></Button>
          </div>
        ))}
        <div className="flex justify-between">
          <Button variant="outline" onClick={() => setDoc({ ...doc, units: [...doc.units, { name: "", sub: "" }] })} data-testid="syl-add-unit"><Plus className="h-4 w-4 mr-1" />Add unit</Button>
          <Button onClick={save} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="syl-save">Save new version</Button>
        </div>
      </Card>}
    </div>
  );
}
